import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { pythonUrl } from '../../config/baseUrl'
import { useSidebarContext } from '../../contexts/Old_SidebarContext'

/* ------------------ CONSTANTS ------------------ */

const MAX_FILE_SIZE = 300 * 1024 * 1024 // 300MB per file
const TOTAL_MAX_SIZE = 300 * 1024 * 1024 // 300MB total
const MAX_FILES = 30

/* ------------------------------------------------ */

export function useFileUpload({
  onStatus,
  setApiResponded,
}: {
  onStatus: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
  setApiResponded: (value: boolean) => void
}) {
  const [files, setFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)

  // Persisted uploaded files
  const [uploadedFiles, setUploadedFiles] = useState<Set<string>>(new Set())
  const [uploadedTotalSize, setUploadedTotalSize] = useState<number>(0)

  // Names of files that were selected before a page refresh (metadata only)
  const [staleFileNames, setStaleFileNames] = useState<string[]>([])

  // Restored uploaded file metadata (shown after refresh when uniqueString exists)
  const [restoredFileMeta, setRestoredFileMeta] = useState<Array<{ name: string; size: number }>>(
    [],
  )

  const abortControllerRef = useRef<AbortController | null>(null)

  const {
    uniqueString,
    setUniqueString,
    deleteUniqueString,
    setIsRefFileUploaded,
    setUploadedFileName,
  } = useSidebarContext()

  /* ------------------ HELPERS ------------------ */

  const getFileSignature = (file: File): string => `${file.name}-${file.size}-${file.lastModified}`

  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const validateFile = (file: File): string | null => {
    if (file.type !== 'application/pdf') return 'Only PDF files allowed'
    if (file.size > MAX_FILE_SIZE) return 'File exceeds limit'
    if (file.size === 0) return 'File is empty'

    return null
  }

  const isDuplicate = (file: File): boolean =>
    files.some(
      (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified,
    )

  /* ------------------ LOAD FROM SESSION STORAGE ------------------ */

  useEffect(() => {
    const persisted = sessionStorage.getItem('uploadedFiles')
    const persistedSize = sessionStorage.getItem('uploadedTotalSize')
    if (persisted) {
      try {
        const parsed = JSON.parse(persisted)
        if (Array.isArray(parsed)) {
          setUploadedFiles(new Set(parsed))
        }
      } catch {}
    }

    if (persistedSize) {
      setUploadedTotalSize(Number(persistedSize))
    }

    // Clean up legacy fileData from sessionStorage (no longer used)
    sessionStorage.removeItem('fileData')

    // Restore uniqueString from sessionStorage (persisted across refresh)
    const savedUniqueString = sessionStorage.getItem('uploadUniqueString')
    const savedUploadedMeta = sessionStorage.getItem('uploadedFileMeta')
    const savedSelectedMeta = sessionStorage.getItem('selectedFileMeta')

    if (savedUniqueString) {
      setUniqueString(savedUniqueString)

      // Restore uploaded file metadata if files were already processed
      let uploadedNames = new Set<string>()
      if (savedUploadedMeta) {
        try {
          const parsed = JSON.parse(savedUploadedMeta)
          if (Array.isArray(parsed) && parsed.length > 0) {
            setRestoredFileMeta(parsed)
            setIsRefFileUploaded(true)
            setUploadedFileName(parsed.map((f: { name: string }) => f.name))
            uploadedNames = new Set(parsed.map((f: { name: string }) => f.name))
          }
        } catch {}
      }

      // Check for files that were selected but not yet uploaded
      if (savedSelectedMeta) {
        try {
          const parsed = JSON.parse(savedSelectedMeta)
          if (Array.isArray(parsed) && parsed.length > 0) {
            const staleNames = parsed
              .filter((f: { name: string }) => !uploadedNames.has(f.name))
              .map((f: { name: string }) => f.name)
            if (staleNames.length > 0) {
              setStaleFileNames(staleNames)
            }
          }
        } catch {}
      }
    } else {
      // No uniqueString means upload never completed — all selected files are stale
      if (savedSelectedMeta) {
        try {
          const parsed = JSON.parse(savedSelectedMeta)
          if (Array.isArray(parsed) && parsed.length > 0) {
            setStaleFileNames(parsed.map((f: { name: string }) => f.name))
          }
        } catch {}
      }
    }
  }, [])

  /* ------------------ SAVE TO SESSION STORAGE ------------------ */

  useEffect(() => {
    sessionStorage.setItem('uploadedFiles', JSON.stringify(Array.from(uploadedFiles)))
    sessionStorage.setItem('uploadedTotalSize', String(uploadedTotalSize))
  }, [uploadedFiles, uploadedTotalSize])

  // Save lightweight file metadata to sessionStorage when files change
  useEffect(() => {
    if (files.length > 0) {
      const meta = files.map((f) => ({ name: f.name, size: f.size }))
      sessionStorage.setItem('selectedFileMeta', JSON.stringify(meta))
      // Clear stale names once real files are present
      setStaleFileNames([])
    } else {
      sessionStorage.removeItem('selectedFileMeta')
    }
  }, [files])

  /* ------------------ ADD FILES ------------------ */

  const addFiles = useCallback(
    (list: FileList) => {
      let currentTotal = files.reduce((s, f) => s + f.size, 0)
      const added: File[] = []

      for (const file of Array.from(list)) {
        if (isDuplicate(file)) {
          onStatus(`File "${file.name}" is already selected.`, 'warning')
          continue
        }

        const error = validateFile(file)

        if (error) {
          if (error.includes('limit')) {
            onStatus(`File "${file.name}" exceeds ${formatBytes(MAX_FILE_SIZE)}.`, 'error')
            setTimeout(() => {
              onStatus('', 'info')
            }, 6000)
          } else {
            onStatus(`File "${file.name}" skipped: ${error}`, 'warning')
          }
          continue
        }

        const projectedTotal = currentTotal + file.size

        // 🔥 NEW: show total-size warning properly
        if (projectedTotal > TOTAL_MAX_SIZE) {
          onStatus(
            `Warning: total file size exceeds limit of ${formatBytes(TOTAL_MAX_SIZE)}.`,
            'warning',
          )
        }

        // 🔥 IMPORTANT: update currentTotal so loop stays accurate
        currentTotal = projectedTotal

        added.push(file)
      }

      if (added.length > 0) {
        setFiles((prev) => [...prev, ...added])
        setProgress(0)
        // Update uploadedFileName with restored + existing + newly added files
        const allNames = [
          ...restoredFileMeta.map((f) => f.name),
          ...files.map((f) => f.name),
          ...added.map((f) => f.name),
        ]
        setUploadedFileName(allNames)
        onStatus(`${added.length} file(s) added`, 'info')
      }
    },
    [files, restoredFileMeta, onStatus, setUploadedFileName],
  )

  /* ------------------ REMOVE FILE ------------------ */

  const removeFile = useCallback(
    (fileToRemove: File) => {
      setFiles((prev) => {
        const updated = prev.filter((f) => f !== fileToRemove)
        const restoredNames = restoredFileMeta.map((f) => f.name)

        if (updated.length === 0 && restoredNames.length === 0) {
          setProgress(0)
          onStatus('', 'info')
          setIsRefFileUploaded(false)
          setUploadedFileName([])
        } else {
          // Update uploadedFileName to reflect restored + remaining files
          setUploadedFileName([...restoredNames, ...updated.map((f) => f.name)])
        }

        return updated
      })

      const sig = getFileSignature(fileToRemove)

      // update uploaded set + size
      setUploadedFiles((prev) => {
        const updated = new Set(prev)
        if (updated.has(sig)) {
          updated.delete(sig)
          setUploadedTotalSize((s) => s - fileToRemove.size)
        }
        return updated
      })

      if (files.length === 1 && uniqueString) {
        deleteUniqueString(uniqueString)
        setUniqueString('')
        sessionStorage.removeItem('uploadUniqueString')
      }

      onStatus(`${fileToRemove.name} removed`, 'info')
    },
    [
      files,
      restoredFileMeta,
      uniqueString,
      deleteUniqueString,
      setUniqueString,
      setIsRefFileUploaded,
      setUploadedFileName,
      onStatus,
    ],
  )

  /* ------------------ UPLOAD ALL ------------------ */

  const uploadAll = useCallback(async () => {
    if (files.length === 0) {
      onStatus('No files to upload', 'warning')
      return
    }
    if (uploading) return

    setProgress(0)
    onStatus('', 'info')

    const filesToUpload = files.filter((f) => !uploadedFiles.has(getFileSignature(f)))

    if (filesToUpload.length === 0) {
      onStatus('No new files to upload', 'info')
      return
    }

    // Unique upload session ID
    const uploadId = uniqueString || crypto.randomUUID()
    if (!uniqueString) setUniqueString(uploadId)

    const formData = new FormData()
    filesToUpload.forEach((f) => {
      formData.append('file', f)
    })
    formData.append('uniqueString', uploadId)

    const controller = new AbortController()
    abortControllerRef.current = controller

    setUploading(true)
    setProgress(0)

    const expectedTotal = filesToUpload.reduce((s, f) => s + f.size, 0)
    let lastPct = 0
    const alpha = 0.25

    try {
      /* -------------------------------------------------------
       * 1️⃣ STEP 1 — UPLOAD + VALIDATE
       * ------------------------------------------------------- */
      const uploadResponse = await axios.post(`${pythonUrl}/pyapi/upload_validate`, formData, {
        signal: controller.signal,
        headers: { usecase: 'upload' },
        onUploadProgress: (e) => {
          const total = e.total && e.total > 0 ? e.total : expectedTotal || e.loaded || 1

          // Cap at 99 so the progress bar stays visible until the server responds
          const rawPct = Math.min(99, Math.round((e.loaded * 100) / total))
          let smoothed = Math.round(alpha * rawPct + (1 - alpha) * lastPct)

          if (smoothed < lastPct) smoothed = lastPct

          lastPct = smoothed
          setProgress(smoothed)
        },
      })

      //  IF backend says status !== true
      interface UploadedFileInfo {
        filename: string
        valid: boolean
        error?: string
      }

      if (uploadResponse?.data?.status !== true) {
        const fileList = uploadResponse?.data?.files as UploadedFileInfo[] | undefined

        // Extract INVALID file errors only
        const fileErrors = fileList
          ?.filter((f) => !f.valid && f.error)
          ?.map((f) => `${String(f.filename)}: ${String(f.error)}`)
          ?.join('\n')

        if (fileErrors) {
          // 🔥 ALWAYS show per-file errors, ignore .message
          onStatus(fileErrors, 'error')
        } else {
          // Fallback ONLY if no file errors exist
          onStatus(uploadResponse?.data?.message ?? 'Validation failed', 'error')
        }

        setUploading(false)
        setProgress(0)
        abortControllerRef.current = null
        setApiResponded(true)

        return
      }

      /* -----------------------------------------------
       * 🔥 IMPORTANT CHANGE
       * Set 100% ONLY IF backend says status = true
       * ----------------------------------------------- */
      if (uploadResponse?.data?.status === true) {
        setProgress(100) // ✅ now correctly placed

        // Mark files as uploaded
        setUploadedFiles((prev) => {
          const updated = new Set(prev)
          filesToUpload.forEach((f) => updated.add(getFileSignature(f)))
          return updated
        })

        setUploadedTotalSize((s) => s + filesToUpload.reduce((sum, f) => sum + f.size, 0))

        // Update context with restored + current file names
        setUploadedFileName([...restoredFileMeta.map((f) => f.name), ...files.map((f) => f.name)])

        setIsRefFileUploaded(true)
        onStatus('Files validated and stored. Processing…', 'success')
      } else {
        onStatus(uploadResponse?.data?.message || 'Validation failed', 'error')
        setUploading(false)
        return
      }

      /* -------------------------------------------------------
       * 2️⃣ STEP 2 — CALL /processing
       * ------------------------------------------------------- */
      const processingResponse = await axios.post(
        `${pythonUrl}/pyapi/processing`,
        { uniqueString: uploadId },
        { signal: controller.signal },
      )

      // Set API responded to true after processing completes
      setApiResponded(true)

      if (processingResponse?.data?.status) {
        // Persist the uniqueString from the API response
        const responseUniqueString = processingResponse.data.uniqueString || uploadId
        setUniqueString(responseUniqueString)
        sessionStorage.setItem('uploadUniqueString', responseUniqueString)

        // Save uploaded file metadata for restore after refresh (include restored + current files)
        const fileMeta = files.map((f) => ({ name: f.name, size: f.size }))
        const uploadedMeta = [...restoredFileMeta, ...fileMeta]
        sessionStorage.setItem('uploadedFileMeta', JSON.stringify(uploadedMeta))

        onStatus('Processing completed successfully', 'success')
      } else {
        // Processing failed - reset state and show error
        const errorMessage = processingResponse?.data?.message || 'Processing failed'
        onStatus(errorMessage, 'error')

        // Reset file upload state to allow re-upload
        setFiles([])
        setProgress(0)
        setUploadedFiles(new Set())
        setUploadedTotalSize(0)
        setIsRefFileUploaded(false)
        setUploadedFileName([])

        if (uniqueString) {
          await deleteUniqueString(uniqueString)
          setUniqueString('')
          sessionStorage.removeItem('uploadUniqueString')
          sessionStorage.removeItem('uploadedFileMeta')
          setRestoredFileMeta([])
        }
      }
    } catch (err: any) {
      // Handle all errors and reset state
      let errorMessage = 'Upload failed'

      if (axios.isCancel(err)) {
        errorMessage = 'Upload canceled'
        onStatus(errorMessage, 'warning')
      } else {
        if (err?.response?.data?.message) {
          errorMessage = err.response.data.message
        } else if (err?.message) {
          errorMessage = `Error: ${String(err.message)}`
        }
        onStatus(errorMessage, 'error')
      }

      // Reset all file upload state to initial state
      setFiles([])
      setProgress(0)
      setUploadedFiles(new Set())
      setUploadedTotalSize(0)
      setIsRefFileUploaded(false)
      setUploadedFileName([])
      setUploading(false)

      if (uniqueString) {
        try {
          await deleteUniqueString(uniqueString)
          setUniqueString('')
          sessionStorage.removeItem('uploadUniqueString')
          sessionStorage.removeItem('uploadedFileMeta')
          setRestoredFileMeta([])
        } catch (deleteError) {
          console.error('Error deleting unique string:', deleteError)
        }
      }
    } finally {
      abortControllerRef.current = null
      setUploading(false)
      setApiResponded(true)
    }
  }, [
    files,
    restoredFileMeta,
    uploading,
    uploadedFiles,
    uniqueString,
    setUniqueString,
    deleteUniqueString,
    setIsRefFileUploaded,
    setUploadedFileName,
    onStatus,
    setApiResponded,
  ])

  /* ------------------ CANCEL UPLOAD ------------------ */

  const cancelUpload = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setUploading(false)
    setProgress(0)
    onStatus('Upload canceled', 'warning')
  }, [onStatus])

  /* ------------------ CLEAR ALL ------------------ */

  const clearAll = useCallback(async () => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null

    setFiles([])
    setUploading(false)
    setProgress(0)
    setUploadedFiles(new Set())
    setUploadedTotalSize(0)
    setIsRefFileUploaded(false)
    setUploadedFileName([])

    sessionStorage.removeItem('uploadedFiles')
    sessionStorage.removeItem('uploadedTotalSize')
    sessionStorage.removeItem('selectedFileMeta')
    sessionStorage.removeItem('uploadedFileMeta')
    setStaleFileNames([])
    setRestoredFileMeta([])

    if (uniqueString) {
      await deleteUniqueString(uniqueString)
      setUniqueString('')
      sessionStorage.removeItem('uploadUniqueString')
    }

    onStatus('Files cleared', 'info')
  }, [
    uniqueString,
    deleteUniqueString,
    setUniqueString,
    setIsRefFileUploaded,
    setUploadedFileName,
    onStatus,
  ])

  /* ------------------ DERIVED ------------------ */

  const totalSelectedSize =
    files.reduce((s, f) => s + f.size, 0) + restoredFileMeta.reduce((s, f) => s + f.size, 0)

  /* ------------------ RETURN ------------------ */

  return {
    files,
    filesCount: files.length,
    uploading,
    progress,
    uploadedFiles,
    uploadedTotalSize,
    totalSelectedSize,
    getFileSignature,
    formatBytes,

    addFiles,
    uploadAll,
    cancelUpload,
    clearAll,
    removeFile,

    restoredFileMeta,

    staleFileNames,
    dismissStaleFiles: () => {
      setStaleFileNames([])
      sessionStorage.removeItem('selectedFileMeta')
    },

    MAX_FILE_SIZE,
    TOTAL_MAX_SIZE,
    MAX_FILES,
  }
}
